import "@sphtech/dmg-design-system/dist/theme-female.css";
import "@sphtech/dmg-design-system/assets/female/fonts.css";

import { buildUrlWithGARefCode } from "@app/utils/ga/tracking";
import AppNewsletter from "@components/Modules/Newsletter/AppNewsletter";
import Newsticker from "@components/Modules/Newsticker/Newsticker";
import {
  configHeader,
  configNewsletter,
  configSingleStory,
  configSite,
  configTrackingCode,
} from "@pub/config";
import { BaseLayout, CustomClassContext } from "@sphtech/dmg-design-system";
import cx from "classnames";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router";

import customStyle from "./custom.module.css";
import logoSvgSource from "./logo.svg?raw";
import logoSvgInverted from "./logo-inverted.svg?raw";
import { RoutePaths } from "./routePaths";

const closeIcon = (
  <svg width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="m13.058 12 7.08-7.08a.747.747 0 1 0-1.058-1.057L12 10.943 4.92 3.855a.747.747 0 1 0-1.057 1.058l7.08 7.08-7.08 7.08a.747.747 0 0 0 .532 1.275.768.768 0 0 0 .533-.218l7.08-7.08 7.08 7.08c.15.15.337.218.532.218a.747.747 0 0 0 .532-1.275l-7.08-7.08-.014.007Z"
      fill="#fff"
    />
  </svg>
);

const searchIcon = (
  <svg width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14.776 8.263a6.504 6.504 0 0 1-6.513 6.513A6.504 6.504 0 0 1 1.75 8.263 6.504 6.504 0 0 1 8.263 1.75a6.504 6.504 0 0 1 6.513 6.513ZM13.04 13.04l5.21 5.21" />
    </g>
  </svg>
);

type BaseLayoutProps = {
  ArticleTitle?: string;
  children: React.ReactNode;
  ads?: boolean;
  leaderboard?: boolean;
  section?: React.ComponentProps<typeof BaseLayout>["section"];
  currentInViewArticleRef?: HTMLElement | null;
};

const FemaleBaseLayout = ({
  ArticleTitle,
  section,
  children,
  ads = true,
  leaderboard = true,
  currentInViewArticleRef,
}: BaseLayoutProps) => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();

  const newsLetterProps = {
    backgroundImage: configNewsletter.image || "",
    newsletterCtaText: "SIGN UP",
    emailPlaceHolder: {
      placeholder: "Email Address",
    },
    newsletterBody: configNewsletter.body || "",
    newsletterTitle: configNewsletter.title || "",
    newsletterLang: configNewsletter.lang || "",
  };

  const SearchFormOnSubmit = (searchTerm?: string) => {
    if (!searchTerm) return;

    window.location.href = `/search?q=${encodeURIComponent(searchTerm)}`;
  };

  return (
    <CustomClassContext.Provider
      value={{
        navbar: {
          logo: customStyle.logo,
          articleSection: customStyle.navBarArticleSection,
        },
        button: {
          primary: customStyle.buttonPrimary,
          secondary: customStyle.buttonSecondary,
        },
        sectionHeading: {
          container: customStyle.sectionHeadingContainer,
        },
        label: customStyle.label,
        tags: {
          wrapper: customStyle.label,
        },
        contentBlock: {
          cta: customStyle.contentBlockCta,
        },
        categoryLayout: {
          sectionContainer: customStyle.anchorBlockHeading,
          anchorBlock: customStyle.anchorBlockText,
          tabContainer: customStyle.categoryTabContainer,
          categoryHeading: customStyle.label,
        },
        homeAnchorBlock: customStyle.anchorBlockText,
        anchorBlock: {
          summaryItem: customStyle.anchorBlockSummaryItem,
        },
        latestArticle: {
          landscape: customStyle.latestArticleLandscape,
        },
        relatedItem: {
          verticalLine: customStyle.relatedItemVerticalLine,
        },
        errorLayout: {
          container: customStyle.errorLayoutContainer,
          errorLayoutHeadingContainer: customStyle.errorLayoutHeadingContainer,
        },
        aboutUs: {
          heading: customStyle.label,
          standfirst: customStyle.anchorBlockText,
        },
        singlePost: {
          readmore: customStyle.readMore,
        },
        contentHubLayout: {
          articleMastHeadText: cx(
            customStyle.contentHubAnchorBlock,
            customStyle.contentHubAnchorBlockText,
          ),
          standfirst: customStyle.anchorBlockText,
        },
        homeContentBlockB: {
          contentBlockScroll: customStyle.homeContentBlockBscroll,
        },
        homeContentBlockF: {
          sectionHeading: customStyle.contentBlockFSectionHeading,
        },
        homeContentBlockG: {
          sectionHeading: customStyle.homeContentBlockGSectionHeading,
        },
      }}
    >
      <BaseLayout
        siteName={configSite.name}
        ArticleTitle={ArticleTitle}
        section={section}
        ads={ads}
        leaderboard={leaderboard}
        NewsTicker={<Newsticker />}
        RoutePaths={RoutePaths}
        svgSource={logoSvgSource}
        footerSvgSource={logoSvgInverted}
        mciText={
          <span suppressHydrationWarning>
            MDDI (P) 032/07/2025. Published by SPH Media Limited, Co. Regn. No.
            202120748H. Copyright © {currentYear} SPH Media Limited. All rights
            reserved.
          </span>
        }
        currentLocation={location}
        socialIconProps={configHeader.socialMedia}
        megaMenuProps={{
          NewsLetterProps: {
            ...configHeader.megaMenu.newsletter,
            url:
              (configTrackingCode.signup &&
                buildUrlWithGARefCode(
                  configHeader.megaMenu.newsletter.url,
                  configTrackingCode.signup,
                )) ||
              configHeader.megaMenu.newsletter.url,
          },
          CloseButtonProps: configHeader.megaMenu.closeButton,
          divider: configHeader.megaMenu.divider?.footer,
          logoSvg: logoSvgInverted,
        }}
        sphLogoInverted={true}
        footerWithDivider={false}
        NewsLetterElement={
          location.pathname !== "/newsletter-signup" && (
            <AppNewsletter {...newsLetterProps} />
          )
        }
        searchBoxProps={{
          searchIcon: searchIcon,
          closeIcon: closeIcon,
          onSubmit: SearchFormOnSubmit,
        }}
        currentInViewArticleRef={currentInViewArticleRef}
        readProgress={configSingleStory.readProgress}
      >
        <Helmet
          htmlAttributes={{
            lang: "en-SG",
          }}
        >
          <link
            rel="icon"
            type="image/png"
            sizes="48x48"
            href="/favicon-48x48.png"
          />
          <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
          <link rel="shortcut icon" href="/favicon.ico" />
          <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#xxx" />
          <meta name="msapplication-TileColor" content="#xxx" />
          <meta name="theme-color" content="#ffffff" />
          <meta
            name="p:domain_verify"
            content="7550a64d81e1c55bca2046c15d0218cc"
          />
        </Helmet>
        {children}
      </BaseLayout>
    </CustomClassContext.Provider>
  );
};

export default FemaleBaseLayout;
